import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import Payment from 'components/FrictionlessCheckout/PaymentReleaseFour/Payment';
import orderUtils from 'utils/Order';
import paymentOptionsSelector from 'selectors/order/paymentOptionsSelector';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import checkoutUtils from 'utils/Checkout';
import pazeSelector from 'selectors/paze/pazeSelector';
import klarnaSelector from 'selectors/klarna/klarnaSelector';
import afterpaySelector from 'selectors/afterpay/afterpaySelector';
import venmoSelector from 'selectors/venmo/venmoSelector';
import paypalSelector from 'selectors/paypal/paypalSelector';
import { orderSelector } from 'selectors/order/orderSelector';
import { userSelector } from 'selectors/user/userSelector';
import {
    checkSelectedPayment,
    getCreditCards,
    commonOrderToggleActions,
    isAlternatePaymentMethodSelected,
    updatePaymentActions,
    payPalWithInterstice,
    sectionSaveOrderAction
} from 'services/Checkout';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import orderErrorsSelector from 'selectors/order/orderErrorsSelector';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import Actions from 'Actions';
import activeCheckoutSectionSelector from 'selectors/order/activeCheckoutSectionSelector';
import OrderActions from 'actions/OrderActions';
import AfterpayActions from 'actions/AfterpayActions';
import KlarnaActions from 'actions/KlarnaActions';
import PazeActions from 'actions/PazeActions';
import * as PayPalActions from 'actions/PayPalActions';
import PaymentsActions from 'actions/PaymentsActions';
import UserActions from 'actions/UserActions';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import PaymentEN from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_en_US';
import PaymentFR from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_fr_CA';
import PaymentUtils from 'utils/payment';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { localeSelector } from 'viewModel/selectors/locale/localeSelector';
import { PAYPAL_LEGAL_LINKS } from 'constants/PayPal';

const PaymentSectionNeedsConfirmationSelector = createSelector(orderSelector, order => order.paymentNeedsToBeConfirmed || false);

const {
    checkGiftCardAmountEqualsOrderTotal,
    checkIsAltenateMethodDefaultPayment,
    getAvailablePaymentMethods,
    getGiftCardPaymentGroup,
    checkIfAlternateMethodSelected,
    getAlternatePaymentName,
    getInstallment,
    isAnyPaymentOptionSelected
} = PaymentUtils;

const { showInfoModal, showContentModal } = Actions;

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PaymentEN, PaymentFR);

const shouldShowSubscriptionPaymentError = ({
    isSubscriptionOrder,
    hasNoSavedCreditCards,
    hasAppliedGiftCard,
    normalizedPendingCreditCard,
    alternateMethodSelected
}) => {
    return isSubscriptionOrder && hasNoSavedCreditCards && hasAppliedGiftCard && !normalizedPendingCreditCard && !alternateMethodSelected;
};

const getEffectivePaymentSectionError = ({ paymentSectionError, shouldShowSubscriptionError, isCreditCardRequiredMessage, fallbackMessage }) => {
    if (paymentSectionError) {
        return paymentSectionError;
    }

    if (shouldShowSubscriptionError) {
        return isCreditCardRequiredMessage || fallbackMessage;
    }

    return null;
};

const localization = createStructuredSelector({
    cardTitle: getTextFromResource(getText, 'cardTitle'),
    expires: getTextFromResource(getText, 'expires'),
    addNewCard: getTextFromResource(getText, 'addNewCard'),
    useGiftCard: getTextFromResource(getText, 'useGiftCard'),
    maxGiftCards: getTextFromResource(getText, 'maxGiftCards'),
    giftCard: getTextFromResource(getText, 'giftCard'),
    applied: getTextFromResource(getText, 'applied'),
    remove: getTextFromResource(getText, 'remove'),
    secure: getTextFromResource(getText, 'secure'),
    verifyCVV: getTextFromResource(getText, 'verifyCVV'),
    accountCredit: getTextFromResource(getText, 'accountCredit'),
    giftCardsNotCombinable: getTextFromResource(getText, 'giftCardsNotCombinable'),
    giftCardsNotCombinableWithThisPayment: getTextFromResource(getText, 'giftCardsNotCombinableWithThisPayment'),
    giftCardAdditionalPaymentRequired: getTextFromResource(getText, 'giftCardAdditionalPaymentRequired'),
    paymentMethodSelection: getTextFromResource(getText, 'paymentMethodSelection'),
    otherWaysToPay: getTextFromResource(getText, 'otherWaysToPay'),
    interestFreeMessage: getTextFromResource(getText, 'interestFreeMessage'),
    orPayInFour: getTextFromResource(getText, 'orPayInFour'),
    installmentsMessage: getTextFromResource(getText, 'installmentsMessage', ['{0}']),
    payWith: getTextFromResource(getText, 'payWith'),
    availableWith: getTextFromResource(getText, 'availableWith'),
    edit: getTextFromResource(getText, 'edit'),
    afterpayHeader: getTextFromResource(getText, 'afterpayHeader'),
    afterpayHeaderCA: getTextFromResource(getText, 'afterpayHeaderCA'),
    afterpaySubtitle: getTextFromResource(getText, 'afterpaySubtitle', ['{0}']),
    afterpaySaveAndContinue: getTextFromResource(getText, 'afterpaySaveAndContinue'),
    afterpayCancel: getTextFromResource(getText, 'afterpayCancel'),
    afterpayLoading: getTextFromResource(getText, 'afterpayLoading'),
    afterpayApiErrorFallback: getTextFromResource(getText, 'afterpayApiErrorFallback'),
    afterpayErrorMessage: getTextFromResource(getText, 'afterpayErrorMessage'),
    afterpayGiftCardWarning: getTextFromResource(getText, 'afterpayGiftCardWarning'),
    afterpayLegalNotice: getTextFromResource(getText, 'afterpayLegalNotice'),
    klarnaHeader: getTextFromResource(getText, 'klarnaHeader'),
    klarnaSubtitle: getTextFromResource(getText, 'klarnaSubtitle', ['{0}']),
    klarnaSaveAndContinue: getTextFromResource(getText, 'klarnaSaveAndContinue'),
    klarnaCancel: getTextFromResource(getText, 'klarnaCancel'),
    klarnaLoading: getTextFromResource(getText, 'klarnaLoading'),
    klarnaApiErrorFallback: getTextFromResource(getText, 'klarnaApiErrorFallback'),
    klarnaGiftCardWarning: getTextFromResource(getText, 'klarnaGiftCardWarning'),
    klarnaFeatureBuyerProtection: getTextFromResource(getText, 'klarnaFeatureBuyerProtection'),
    klarnaTermsText: getTextFromResource(getText, 'klarnaTermsText'),
    // For fr_CA we read a hardcoded key that has no _en_US.js counterpart so the SDL translation bot cannot overwrite the legally-reviewed copy.
    klarnaLegalNotice: createSelector(localeSelector, () =>
        getText(LanguageLocaleUtils.isFRCanada() ? 'klarnaLegalNoticeHardcoded' : 'klarnaLegalNotice')
    ),
    klarnaBillingAddressCheckbox: getTextFromResource(getText, 'klarnaBillingAddressCheckbox'),
    pazeHeader: getTextFromResource(getText, 'pazeHeader'),
    pazeSubtitle: getTextFromResource(getText, 'pazeSubtitle'),
    pazeGiftCardWarning: getTextFromResource(getText, 'pazeGiftCardWarning'),
    pazeSaveAndContinue: getTextFromResource(getText, 'pazeSaveAndContinue'),
    pazeCancel: getTextFromResource(getText, 'pazeCancel'),
    paypalHeader: getTextFromResource(getText, 'paypalHeader'),
    paypalLegalNotice: createSelector(localeSelector, () => {
        const isCA = LanguageLocaleUtils.isCanada();
        const isFR = LanguageLocaleUtils.isFRCanada();

        let localeKey;

        if (!isCA) {
            localeKey = 'us';
        } else if (isFR) {
            localeKey = 'caFr';
        } else {
            localeKey = 'caEn';
        }

        return getText('paypalLegalNotice', [PAYPAL_LEGAL_LINKS.terms[localeKey], PAYPAL_LEGAL_LINKS.privacy[localeKey]]);
    }),
    paypalSaveAndContinue: getTextFromResource(getText, 'paypalSaveAndContinue'),
    paypalCancel: getTextFromResource(getText, 'paypalCancel'),
    cantUseForThisOrder: getTextFromResource(getText, 'cantUseForThisOrder'),
    cannotBeCombinedWithGiftCards: getTextFromResource(getText, 'cannotBeCombinedWithGiftCards'),
    cannotBeUsedWhenPurchasingGiftCards: getTextFromResource(getText, 'cannotBeUsedWhenPurchasingGiftCards'),
    seeDetails: getTextFromResource(getText, 'seeDetails'),
    alternatePaymentDetailsTitle: getTextFromResource(getText, 'alternatePaymentDetailsTitle'),
    alternatePaymentDetailsMessage: getTextFromResource(getText, 'alternatePaymentDetailsMessage'),
    gotIt: getTextFromResource(getText, 'gotIt'),
    and: getTextFromResource(getText, 'and'),
    accountCreditApplied: getTextFromResource(getText, 'accountCreditApplied'),
    accountCreditCoversFullOrder: getTextFromResource(getText, 'accountCreditCoversFullOrder'),
    accountCreditLessThanTotal: getTextFromResource(getText, 'accountCreditLessThanTotal'),
    storeCreditNotEnoughMessage: getTextFromResource(getText, 'storeCreditNotEnoughMessage'),
    accountCreditSubscriptionMessage: getTextFromResource(getText, 'accountCreditSubscriptionMessage'),
    subscriptionCreditCardRequired: getTextFromResource(getText, 'subscriptionCreditCardRequired'),
    storeCreditAndGCNotEnoughMessage: getTextFromResource(getText, 'storeCreditAndGCNotEnoughMessage'),
    maximumReached: getTextFromResource(getText, 'maximumReached'),
    maxCardsReachedMessage: getTextFromResource(getText, 'maxCardsReachedMessage'),
    ok: getTextFromResource(getText, 'ok')
});

const fields = createSelector(
    localization,
    paymentOptionsSelector,
    orderDetailsSelector,
    pazeSelector,
    klarnaSelector,
    afterpaySelector,
    venmoSelector,
    paypalSelector,
    orderErrorsSelector,
    activeCheckoutSectionSelector,
    orderSelector,
    userSelector,
    PaymentSectionNeedsConfirmationSelector,
    (_ownState, ownProps) => ownProps.isNewUserFlow,
    (_ownState, ownProps) => ownProps.hasSDUInBasket,
    (_ownState, ownProps) => ownProps.hasAutoReplenishment,
    // eslint-disable-next-line complexity
    (
        locale,
        paymentOptions,
        orderDetails,
        paze,
        klarna,
        afterpay,
        venmo,
        paypal,
        sectionErrors,
        activeSection,
        order,
        user,
        paymentSectionNeedsConfirmation,
        isNewUserFlow,
        hasSDUInBasket,
        hasAutoReplenishment
    ) => {
        const creditCardPaymentGroup = orderUtils.getCreditCardPaymentGroup(orderDetails);
        const normalizedPendingCreditCard =
            order.pendingCreditCard?.cardNumber && order.pendingCreditCard?.expirationMonth && order.pendingCreditCard?.expirationYear
                ? order.pendingCreditCard
                : null;
        // Use pending credit card only when it has valid card details; otherwise use server data
        const displayCreditCard = normalizedPendingCreditCard || creditCardPaymentGroup;
        const storeCredits = FrictionlessUtils.mergeStoreCreditsData(user?.storeCredits || [], orderUtils.getStoreCredits(orderDetails));
        const isPaymentComplete = checkoutUtils.isPaymentInOrderComplete(false);
        const giftCardPaymentGroups = getGiftCardPaymentGroup(orderDetails.paymentGroups);
        const availablePaymentMethods = getAvailablePaymentMethods(orderDetails, isNewUserFlow, klarna.error);
        const payPalPaymentGroup = orderUtils.getPayPalPaymentGroup(orderDetails) || { isComplete: false };
        const venmoUsername = orderUtils.getPaymentGroup(orderDetails, 'VenmoPaymentGroup')?.username;
        const isPayPalEnabled = orderUtils.isPayPalEnabled(orderDetails);
        // In Release 4, CVV confirmation for $0 orders (750+ reward points) is deferred to Place Order click via modal.
        // Therefore, zeroDollarHigherRedeemPointsCVV should NOT trigger the inline error icon on Payment L1 in R4.
        const showErrorIcon = checkoutUtils.renderFrictionlessPaymentErrorView();
        const alternatePaymentName = getAlternatePaymentName(afterpay, klarna, venmo, paze, isNewUserFlow);
        const physicalGiftCardShippingGroup = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
        const hardGoodShippingGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
        const isPhysicalGiftCardOnly = physicalGiftCardShippingGroup && !hardGoodShippingGroup;
        const isGiftCardOnly = orderUtils.isGiftCardOnly();
        const isZeroCheckout = orderUtils.isZeroCheckout();
        const isZeroDollarOrderWithCVVValidation = orderUtils.isZeroDollarOrderWithCVVValidation();
        const shouldRenderGiftCardSection = !isPhysicalGiftCardOnly && !isZeroCheckout;
        const paymentSectionError = sectionErrors?.[SECTION_NAMES.PAYMENT];
        const isCreditCardRequiredMessage = checkoutUtils.getCreditCardRequiredMessage(orderDetails?.paymentGroups?.paymentMessages);

        const tmpCardMessage = orderUtils.getTempSephoraCardMessage();
        const gcAmountNotEnoughMessage = checkoutUtils.getGiftCardAmountNotEnoughMessage(orderDetails?.paymentGroups?.paymentMessages);
        const isPhysicalGiftCardAndHardGoodShippingGroup = !!physicalGiftCardShippingGroup && !!hardGoodShippingGroup;
        const giftCardAmountEqualsOrderTotal = checkGiftCardAmountEqualsOrderTotal(orderDetails?.priceInfo);
        const hasNoPaymentOptionSelected = !isAnyPaymentOptionSelected(orderDetails.paymentGroups, normalizedPendingCreditCard);
        const alternateMethodSelected = checkIfAlternateMethodSelected(afterpay, klarna, paze, venmo) && !normalizedPendingCreditCard;
        const hasSavedOrDefaultAlternatePayment = FrictionlessUtils.hasSavedOrDefaultAlternatePayment(paymentOptions, orderDetails);
        const isSubscriptionOrder = PaymentUtils.isSubscriptionOrder(orderDetails);
        const hasNoSavedCreditCards = !paymentOptions?.creditCards || paymentOptions.creditCards.length === 0;
        const hasAppliedGiftCard = giftCardPaymentGroups.length > 0;
        const shouldShowSubscriptionError = shouldShowSubscriptionPaymentError({
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            hasAppliedGiftCard,
            normalizedPendingCreditCard,
            alternateMethodSelected
        });
        const effectivePaymentSectionError = getEffectivePaymentSectionError({
            paymentSectionError,
            shouldShowSubscriptionError,
            isCreditCardRequiredMessage,
            fallbackMessage: locale.giftCardAdditionalPaymentRequired
        });
        const sectionLevelError =
            (effectivePaymentSectionError?.length || (typeof effectivePaymentSectionError === 'boolean' && effectivePaymentSectionError)) &&
            effectivePaymentSectionError;

        const creditCardRequiredMessage =
            orderDetails?.paymentGroups?.paymentMessages?.[0] && orderDetails?.paymentGroups?.paymentMessages?.[0].messages?.[0];

        const shouldDisplayAutoReplenMessage = creditCardRequiredMessage && hasAutoReplenishment;
        const shouldDisplaySDUMessage = creditCardRequiredMessage && hasSDUInBasket;

        return {
            localization: locale,
            orderDetails,
            paymentOptions,
            creditCardPaymentGroup: displayCreditCard,
            isPaymentComplete,
            storeCredits,
            giftCardPaymentGroups,
            alternateMethodSelected,
            checkSelectedPayment: checkSelectedPayment(paymentOptions, availablePaymentMethods),
            availablePaymentMethods,
            isPaypalSelected: paypal?.isSelected || (isPayPalEnabled && payPalPaymentGroup.isComplete),
            isPaypalReady: paypal?.isReady,
            payPalPaymentGroup,
            venmoUsername,
            alternatePaymentName,
            installmentValue: getInstallment(orderDetails),
            isCombinableGiftCards: alternatePaymentName.length === 0 || alternatePaymentName === PAYMENT_TYPES.PAY_VENMO,
            isAlternateMethodDefaultPayment: checkIsAltenateMethodDefaultPayment(alternatePaymentName, paymentOptions?.defaultPayment),
            showErrorIcon,
            shouldRenderGiftCardSection,
            sectionLevelError,
            tmpCardMessage,
            activeSection,
            getCreditCards,
            orderId: orderDetails?.header?.orderId,
            gcAmountNotEnoughMessage,
            isPhysicalGiftCardOnly,
            isGiftCardOnly,
            physicalGiftCardShippingGroup,
            isPhysicalGiftCardAndHardGoodShippingGroup,
            giftCardAmountEqualsOrderTotal,
            hasNoPaymentOptionSelected,
            isAlternatePaymentMethodSelected,
            klarna,
            afterpay,
            paypal,
            selectedAsDefaultPaymentName: user?.selectedAsDefaultPaymentName,
            paymentSectionNeedsConfirmation,
            pendingCreditCard: order?.pendingCreditCard,
            hasSavedOrDefaultAlternatePayment,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            isZeroDollarOrderWithCVVValidation,
            shouldDisplayAutoReplenMessage,
            shouldDisplaySDUMessage
        };
    }
);

const utilityFunctions = {
    payPalWithInterstice
};

const functions = {
    showInfoModal,
    showContentModal,
    setActiveSection: OrderActions.setCheckoutActiveSection,
    setCheckoutSectionErrors: OrderActions.setCheckoutSectionErrors,
    clearCheckoutSectionErrors: OrderActions.clearCheckoutSectionErrors,
    commonOrderToggleActions,
    updatePaymentActions,
    dispatchOrderUpdate: OrderActions.updateOrder,
    goToURL: historyLocationActions.goTo,
    loadExpandedViewWidget: AfterpayActions.loadExpandedViewWidget,
    savePendingCreditCard: OrderActions.savePendingCreditCard,
    klarnaSetReady: KlarnaActions.setReady,
    afterpaySetReady: AfterpayActions.setReady,
    setPaymentMethodAfterPay: AfterpayActions.setPaymentMethod,
    paypalToggleSelect: PayPalActions.toggleSelect,
    paypalSetReady: PayPalActions.setReady,
    paypalShowError: PayPalActions.showError,
    paypalResetError: PayPalActions.resetError,
    removeDefaultPaymentFromProfile: PaymentsActions.removeDefaultPaymentFromProfile,
    toggleSelectAsDefaultPayment: UserActions.toggleSelectAsDefaultPayment,
    clearPendingCreditCard: OrderActions.clearPendingCreditCard,
    loadPaze: PazeActions.loadIframe,
    setPaymentMethodNeedsConfirmation: OrderActions.setPaymentMethodNeedsConfirmation,
    clearNamedSectionErrors: OrderActions.clearNamedSectionErrors,
    sectionSaveOrderAction
};

const withPaymentProps = wrapHOC(
    connect(fields, functions, (stateProps, dispatchProps, ownProps) => ({
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        ...utilityFunctions
    }))
);

export default withPaymentProps(Payment);
